'use client'

import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import type { LayoutStyle, LayoutStyleContextValue } from '@/types/layout'

const LayoutStyleContext = createContext<LayoutStyleContextValue | undefined>(undefined)

/**
 * Persists and applies the active visual layout style to the root element.
 */
export const LayoutStyleProvider = ({ children }: { children: ReactNode }) => {
    const [layoutStyle, setLayoutStyle] = useState<LayoutStyle>(() => {
        if (typeof window === 'undefined') {
            return 'classic'
        }
        const stored = window.localStorage.getItem('layout-style')
        return stored === 'classic' || stored === 'neumorphism' ? stored : 'classic'
    })

    useEffect(() => {
        document.documentElement.classList.remove('theme-classic', 'theme-neumorphism')
        document.documentElement.classList.add(`theme-${layoutStyle}`)
        window.localStorage.setItem('layout-style', layoutStyle)
    }, [layoutStyle])

    /**
     * Switches between the two supported layout styles.
     */
    const toggleLayoutStyle = () => {
        setLayoutStyle((previous) => (previous === 'classic' ? 'neumorphism' : 'classic'))
    }

    return (
        <LayoutStyleContext.Provider value={{ layoutStyle, toggleLayoutStyle }}>{children}</LayoutStyleContext.Provider>
    )
}

export const useLayoutStyle = () => {
    const context = useContext(LayoutStyleContext)
    if (context === undefined) {
        throw new Error('useLayoutStyle must be used within a LayoutStyleProvider')
    }
    return context
}
